import { ArrowDownLeft, ArrowLeftRight, BookCopy, Search, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import PaymentSplitInfo from '../components/PaymentSplitInfo';
import { paymentService } from '../../../shared/services';
import type { PaymentHistoryEntry } from '../../../shared/services/paymentService';
import { useShop } from '../../../shared/store';

interface VendorNavConfig extends Omit<DashboardNavItem, 'label'> {
  labelKey?: string;
  label?: string;
}

const navItemsConfig: VendorNavConfig[] = [
  { id: 'overview', label: 'Overview', to: '/vendor/dashboard/overview', icon: BookCopy },
  { id: 'transfer', label: 'Transfers & Earnings (70%)', to: '/vendor/dashboard/transfers', icon: ArrowLeftRight },
  { id: 'revenue', label: 'Revenue', to: '/vendor/dashboard/revenue', icon: Wallet },
];

interface FilterOptions {
  searchValue: string;
  dateRange: 'all' | 'today' | 'week' | 'month';
}

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
};

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const getStatusColor = (status: string) => {
  if (status === 'successful') return 'bg-emerald-100 text-emerald-700';
  if (status === 'processing') return 'bg-indigo-100 text-indigo-700';
  if (status === 'pending') return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

export const VendorTransferPage = () => {
  const { t } = useTranslation();
  const { section = 'transfers' } = useParams<{ section?: string }>();
  const { currentUser } = useShop();

  const [transactions, setTransactions] = useState<PaymentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    searchValue: '',
    dateRange: 'all',
  });
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentHistoryEntry | null>(null);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await paymentService.getHistory();

      if (result.success && result.data) {
        setTransactions(result.data);
      } else {
        setError('Failed to load transactions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const filteredTransactions = useMemo(() => {
    let result = transactions.filter((tx) => tx.status === 'successful');

    // Filter by date range
    if (filters.dateRange !== 'all') {
      const now = new Date();

      if (filters.dateRange === 'today') {
        result = result.filter((tx) => {
          const date = new Date(tx.createdAt);
          return date.toDateString() === now.toDateString();
        });
      } else if (filters.dateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        result = result.filter((tx) => new Date(tx.createdAt) >= weekAgo);
      } else if (filters.dateRange === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        result = result.filter((tx) => new Date(tx.createdAt) >= monthAgo);
      }
    }

    // Filter by search
    if (filters.searchValue) {
      const search = filters.searchValue.toLowerCase();
      result = result.filter(
        (tx) =>
          tx.customer.name.toLowerCase().includes(search) ||
          tx.customer.email.toLowerCase().includes(search) ||
          tx.transactionRef.toLowerCase().includes(search),
      );
    }

    return result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [transactions, filters]);

  const stats = useMemo(() => {
    const vendorEarnings = filteredTransactions.reduce((sum, tx) => sum + (tx.creatorEarnings ?? tx.amount - Math.round(tx.amount * 0.3)), 0);
    const platformFees = filteredTransactions.reduce((sum, tx) => sum + (tx.platformCommission ?? Math.round(tx.amount * 0.3)), 0);
    const transactionCount = filteredTransactions.length;

    return {
      vendorEarnings,
      platformFees,
      transactionCount,
      totalRevenue: vendorEarnings + platformFees,
    };
  }, [filteredTransactions]);

  const navItems = navItemsConfig.map((config) => ({
    id: config.id,
    label: config.label || config.labelKey || config.id,
    to: config.to,
    icon: config.icon,
  }));
  const activeNavId = navItems.some((item) => item.id === section) ? section : 'transfer';

  return (
    <DashboardFrame
      brand={t('dashboardVendor.brand', 'Haapa Store')}
      roleLabel={t('dashboardVendor.roleLabel', 'Vendor')}
      userName={currentUser?.name ?? t('dashboardVendor.defaultUserName', 'Vendor')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle={t('dashboardVendor.sections.transfer.title', 'Your Earnings & Transfers (70%)')}
      pageDescription={t('dashboardVendor.sections.transfer.description', 'Track your earnings and payouts from successful sales')}
      navItems={navItems}
      activeNavId={activeNavId}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="rounded-3xl bg-gradient-to-r from-emerald-500 to-green-600 p-6 text-white shadow-lg sm:p-8">
          <h1 className="text-3xl font-bold">Your Earnings & Transfers (70%)</h1>
          <p className="mt-2 text-emerald-100">Track your earnings and payouts from successful product sales</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Your Earnings (70%)</p>
            <p className="mt-2 text-2xl font-bold text-emerald-600">{formatCurrency(stats.vendorEarnings, 'RWF')}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Platform Fee (30%)</p>
            <p className="mt-2 text-2xl font-bold text-red-600">{formatCurrency(stats.platformFees, 'RWF')}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Total Revenue</p>
            <p className="mt-2 text-2xl font-bold text-blue-600">{formatCurrency(stats.totalRevenue, 'RWF')}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-600">Sales Count</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">{stats.transactionCount}</p>
          </div>
        </div>

        {/* Filters & Transactions */}
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-3 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by buyer name, email, or transaction ref..."
                value={filters.searchValue}
                onChange={(e) => setFilters({ ...filters, searchValue: e.target.value })}
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 focus:border-emerald-400 focus:outline-none"
              />
            </div>

            {/* Transactions List */}
            {loading ? (
              <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-emerald-500" />
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
                <Wallet className="mx-auto h-12 w-12 text-slate-400" />
                <p className="mt-4 text-slate-600">No sales match your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((transaction, index) => {
                  const platformCommission = transaction.platformCommission ?? Math.round(transaction.amount * 0.3);
                  const creatorEarnings = transaction.creatorEarnings ?? transaction.amount - platformCommission;
                  const transactionKey = `${transaction.id}-${transaction.transactionRef}-${transaction.createdAt}-${index}`;

                  return (
                    <div
                      key={transactionKey}
                      className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-emerald-300 hover:shadow-md sm:p-5"
                      onClick={() => setSelectedTransaction(transaction)}
                    >
                      <div className="flex items-start justify-between gap-4 sm:flex-row">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                              <ArrowDownLeft className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-900">{transaction.customer.name}</p>
                              <p className="truncate text-sm text-slate-500">{transaction.customer.email}</p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-xs font-medium text-slate-500">{transaction.transactionRef}</span>
                            <span className="text-xs text-slate-500">{formatDate(transaction.createdAt)}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">
                            {formatCurrency(creatorEarnings, transaction.currency)}
                          </p>
                          <p className="text-xs text-slate-500">Your 70%</p>
                          <span className={`inline-block mt-1 rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                            {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Sidebar - Filters & Details */}
          <div className="space-y-4">
            {/* Filters */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-semibold text-slate-900">Filters</h3>

              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-600">Date Range</label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as any })}
                    className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Transaction Details */}
            {selectedTransaction && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900">Sale Details</h3>

                <div className="mt-4 space-y-3 text-sm">
                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Buyer</p>
                    <p className="mt-1 font-semibold text-slate-900">{selectedTransaction.customer.name}</p>
                    <p className="text-xs text-slate-500">{selectedTransaction.customer.email}</p>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Sale Amount</p>
                    <p className="mt-1 font-bold text-slate-900">
                      {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                    </p>
                  </div>

                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs text-slate-600">Transaction Reference</p>
                    <p className="mt-1 font-mono font-semibold text-slate-900">{selectedTransaction.transactionRef}</p>
                  </div>
                </div>

                {/* Payment Split Details */}
                <div className="mt-4">
                  <PaymentSplitInfo
                    amount={selectedTransaction.amount}
                    platformCommission={selectedTransaction.platformCommission}
                    creatorEarnings={selectedTransaction.creatorEarnings}
                    currency={selectedTransaction.currency}
                    direction="incoming"
                  />
                </div>

                <div className="mt-4 rounded-lg bg-emerald-50 p-3">
                  <p className="text-xs font-semibold text-emerald-700">Your Earnings (Vendor 70%)</p>
                  <p className="mt-1 text-lg font-bold text-emerald-600">
                    {formatCurrency(
                      selectedTransaction.creatorEarnings ?? selectedTransaction.amount - Math.round(selectedTransaction.amount * 0.3),
                      selectedTransaction.currency,
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardFrame>
  );
};

export default VendorTransferPage;
